// Unified alert store — EWMA Motor Health + What-If simulation

import { ConfidenceBreakdown } from './ewmaEngine';
import { FailurePattern } from './patternEngine';
import { CorrelationResult } from './correlationEngine';

export type AlertSeverity = 'Low' | 'Medium' | 'High' | 'Critical';
export type AlertSource   = 'ewma' | 'whatif' | 'slider';
export type AlertStage    = 'EARLY' | 'MID' | 'LATE';

export interface SensorDetail {
  name:      string;
  sensorId:  string;
  machineId: string;
  ewma:      number;
  slope:     number;
  stage:     AlertStage | 'NORMAL' | 'CALIBRATING';
  mu:        number;
  ucl2:      number;
  ucl3:      number;
}

export interface UnifiedAlert {
  id:                string;
  source:            AlertSource;
  stage?:            AlertStage;
  sensorName:        string;
  machineName:       string;
  currentValue:      number;
  unit:              string;
  ewmaTrend?:        number;
  slope?:            number;
  cycletime?:        number;
  ctBaseline?:       number;
  mu?:               number;
  ucl2?:             number;
  ucl3?:             number;
  ttf?:              number;
  healthScore?:         number;
  confidence?:          number;
  confidenceBreakdown?: ConfidenceBreakdown;
  avgSlope?:            number;
  sensorDetails?:       SensorDetail[];
  detectedPatterns?:    FailurePattern[];
  baselineShiftSensors?: { name: string; delta: number; prevMu: number; newMu: number }[];
  threshold:         { min: number; max: number };
  severity:          AlertSeverity;
  timestamp:         string;
  recommendedAction: string;
  failureProb:       number;
  rul:               number;
}

type Listener = () => void;

const STAGE_SEVERITY: Record<AlertStage, AlertSeverity> = {
  EARLY: 'Low', MID: 'High', LATE: 'Critical',
};
const STAGE_ACTION: Record<AlertStage, string> = {
  EARLY: 'Wear detected — monitor closely and plan inspection',
  MID:   'Real degradation occurring — schedule maintenance soon',
  LATE:  'CRITICAL — Stop equipment immediately and inspect',
};
const STAGE_LABEL: Record<AlertStage, string> = {
  EARLY: 'Early wear detected', MID: 'Active degradation', LATE: 'Imminent failure',
};

export class AlertStore {
  private ewmaAlerts:      Map<string, UnifiedAlert> = new Map();
  private whatifAlert:     UnifiedAlert | null = null;
  private sliderAlerts:    Map<string, UnifiedAlert> = new Map();
  private correlation:     CorrelationResult | null = null;
  private listeners:       Set<Listener> = new Set();

  setEWMAAlert(alert: UnifiedAlert) { this.ewmaAlerts.set(alert.id, alert); this.notify(); }
  clearEWMAAlert(id: string) { if (this.ewmaAlerts.delete(id)) this.notify(); }
  clearAllEWMA() { this.ewmaAlerts.clear(); this.notify(); }
  setWhatIfAlert(alert: UnifiedAlert | null) { this.whatifAlert = alert; this.notify(); }
  setSliderAlert(alert: UnifiedAlert) { this.sliderAlerts.set(alert.id, alert); this.notify(); }
  clearSliderAlert(id: string) { if (this.sliderAlerts.delete(id)) this.notify(); }
  clearAllSlider() { this.sliderAlerts.clear(); this.notify(); }
  setCorrelation(r: CorrelationResult | null) { this.correlation = r; this.notify(); }
  getCorrelation(): CorrelationResult | null { return this.correlation; }

  getAlerts(): UnifiedAlert[] {
    const order = { Critical: 0, High: 1, Medium: 2, Low: 3 };
    const all = [...this.ewmaAlerts.values(), ...this.sliderAlerts.values()];
    if (this.whatifAlert) all.push(this.whatifAlert);
    return all.sort((a, b) => order[a.severity] - order[b.severity]);
  }

  hasAny() { return this.ewmaAlerts.size > 0 || this.whatifAlert !== null; }

  subscribe(fn: Listener) {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  }

  private notify() { this.listeners.forEach(fn => fn()); }

  static buildFusedAlert(params: {
    machineId:           string;
    machineName:         string;
    stage:               AlertStage;
    dominantSensor:      string;
    healthScore:         number;
    confidence:          number;
    confidenceBreakdown: ConfidenceBreakdown;
    avgSlope:            number;
    ttf:                 number;
    sensorResults:       Record<string, any>;
    sensorIdMap:         Record<string, string>;
    detectedPatterns:    FailurePattern[];
    baselineShiftSensors: { name: string; delta: number; prevMu: number; newMu: number }[];
  }): UnifiedAlert {
    const severity = STAGE_SEVERITY[params.stage];
    const fp  = params.stage === 'LATE' ? Math.min(70 + params.avgSlope * 500, 98)
              : params.stage === 'MID'  ? Math.min(40 + params.avgSlope * 500, 69)
              :                           Math.min(10 + params.avgSlope * 500, 39);
    const rul = params.ttf > 0 ? Math.round(params.ttf / 30)
              : params.stage === 'LATE' ? 1
              : params.stage === 'MID'  ? 7 : 30;

    const sensorDetails: SensorDetail[] = Object.entries(params.sensorResults)
      .filter(([, r]) => !r.calibrating)
      .map(([name, r]) => ({
        name,
        sensorId:  params.sensorIdMap[name] || name,
        machineId: params.machineId,
        ewma:  +r.ewma.toFixed(3),
        slope: +r.slope.toFixed(4),
        stage: r.stage,
        mu:    +r.mu.toFixed(3),
        ucl2:  +r.ucl2.toFixed(3),
        ucl3:  +r.ucl3.toFixed(3),
      }));

    const dom = params.sensorResults[params.dominantSensor];
    return {
      id:                `fused-${params.machineId}`,
      source:            'ewma',
      stage:             params.stage,
      sensorName:        params.dominantSensor || 'Multiple Sensors',
      machineName:       params.machineName,
      currentValue:      dom ? +dom.ewma.toFixed(2) : 0,
      unit:              '',
      ewmaTrend:         dom ? +dom.ewma.toFixed(3) : undefined,
      slope:             +params.avgSlope.toFixed(4),
      mu:                dom ? +dom.mu.toFixed(3) : undefined,
      ucl2:              dom ? +dom.ucl2.toFixed(3) : undefined,
      ucl3:              dom ? +dom.ucl3.toFixed(3) : undefined,
      ttf:               params.ttf,
      healthScore:       params.healthScore,
      confidence:        params.confidence,
      confidenceBreakdown: params.confidenceBreakdown,
      avgSlope:          +params.avgSlope.toFixed(4),
      sensorDetails,
      detectedPatterns:     params.detectedPatterns,
      baselineShiftSensors: params.baselineShiftSensors,
      threshold:         { min: 0, max: 100 },
      severity,
      timestamp:         new Date().toLocaleTimeString(),
      recommendedAction: STAGE_ACTION[params.stage],
      failureProb:       Math.round(fp),
      rul,
    };
  }

  static buildMotorAlert(params: {
    machineId:  string; machineName: string;
    sensorId:   string; sensorName:  string;
    value:      number; unit:        string;
    ewma:       number; slope:       number;
    cycletime:  number; ctBaseline:  number;
    mu:         number; ucl2:        number; ucl3: number;
    ttf:        number; stage:       AlertStage;
    min:        number; max:         number;
  }): UnifiedAlert {
    const severity = STAGE_SEVERITY[params.stage];
    const fp  = params.stage === 'LATE' ? Math.min(70 + params.slope * 500, 98)
              : params.stage === 'MID'  ? Math.min(40 + params.slope * 500, 69)
              :                           Math.min(10 + params.slope * 500, 39);
    const rul = params.ttf > 0 ? Math.round(params.ttf / 30)
              : params.stage === 'LATE' ? 1
              : params.stage === 'MID'  ? 7 : 30;
    return {
      id:                `ewma-${params.machineId}-${params.sensorId}`,
      source:            'ewma',
      stage:             params.stage,
      sensorName:        params.sensorName,
      machineName:       params.machineName,
      currentValue:      +params.value.toFixed(2),
      unit:              params.unit,
      ewmaTrend:         +params.ewma.toFixed(3),
      slope:             +params.slope.toFixed(4),
      cycletime:         +params.cycletime.toFixed(2),
      ctBaseline:        params.ctBaseline,
      mu:                +params.mu.toFixed(3),
      ucl2:              +params.ucl2.toFixed(3),
      ucl3:              +params.ucl3.toFixed(3),
      ttf:               params.ttf,
      threshold:         { min: params.min, max: params.max },
      severity,
      timestamp:         new Date().toLocaleTimeString(),
      recommendedAction: STAGE_ACTION[params.stage],
      failureProb:       Math.round(fp),
      rul,
    };
  }

  static stageLabel(stage: AlertStage) { return STAGE_LABEL[stage]; }
}

export const alertStore = new AlertStore();
